/**
 * AppLauncherTile Component
 *
 * Individual app tile for the App Launcher grid.
 * Renders a clickable tile with an icon, label, and optional badge.
 *
 * Features:
 * - Dynamic icon rendering from lucide-react
 * - Optional badge display (e.g., 'New', 'Beta')
 * - Hover effects for visual feedback
 * - Accessible button with proper roles
 */

import React from 'react';
import * as LucideIcons from 'lucide-react';
import type { AppGridItem } from '@features/admin/types/adminEditor';

// ============================================================================
// Icon Helper
// ============================================================================

/**
 * Get a Lucide icon component by name string.
 * Falls back to HelpCircle if the icon name is invalid.
 *
 * @param iconName - Name of the lucide-react icon (e.g., 'LayoutDashboard')
 * @returns Lucide icon component
 */
const getIcon = (iconName: string): React.ElementType => {
  const IconComponent = LucideIcons[iconName as keyof typeof LucideIcons] as React.ElementType;
  return IconComponent || LucideIcons.HelpCircle;
};

// ============================================================================
// Component Props
// ============================================================================

export interface AppLauncherTileProps {
  /** App grid item data */
  item: AppGridItem;
  /** Callback when tile is clicked */
  onClick: () => void;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Individual app tile component for the App Launcher.
 *
 * @example
 * ```tsx
 * <AppLauncherTile
 *   item={{
 *     id: 'dashboard',
 *     label: 'Dashboard',
 *     icon: 'LayoutDashboard',
 *     route: '/app/dashboard',
 *     visible: true,
 *     order: 0,
 *     isSystem: true,
 *   }}
 *   onClick={() => navigate('/app/dashboard')}
 * />
 * ```
 */
export const AppLauncherTile: React.FC<AppLauncherTileProps> = ({ item, onClick }) => {
  const Icon = getIcon(item.icon);

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center justify-center p-3 rounded-lg hover:bg-brand-500/10 transition-all duration-150 cursor-pointer relative group"
      title={item.description}
      aria-label={`Open ${item.label}`}
    >
      {/* Icon */}
      <Icon className="w-8 h-8 text-brand-500 mb-2 group-hover:scale-110 transition-transform" />

      {/* Label */}
      <span className="text-xs text-dark-text-primary text-center truncate max-w-[80px]">
        {item.label}
      </span>

      {/* Optional Badge */}
      {item.badge && (
        <span className="absolute top-1 right-1 text-[10px] font-medium bg-brand-500 text-white px-1.5 py-0.5 rounded-full">
          {item.badge}
        </span>
      )}
    </button>
  );
};

export default AppLauncherTile;
